import { Network } from "@capacitor/network";
import { type ReactNode, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { getRepository } from "../data/createRepository";
import { flushOutbox } from "../lib/outbox";
import { Brand } from "./Brand";
import { CaptureIcon, HomeIcon, PlanIcon, SettingsIcon, VendorsIcon } from "./Icons";
import { UpdatePrompt } from "./UpdatePrompt";

const navItems = [
  { to: "/", label: "Today", icon: HomeIcon, end: true },
  { to: "/capture", label: "Capture", icon: CaptureIcon, end: false },
  { to: "/accounts", label: "Accounts", icon: VendorsIcon, end: false },
  { to: "/field", label: "Plan", icon: PlanIcon, end: false },
  { to: "/settings", label: "Settings", icon: SettingsIcon, end: false },
];

const mobileNavItems = ["/", "/capture", "/accounts", "/field"].flatMap((to) =>
  navItems.filter((item) => item.to === to),
);

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [signOutError, setSignOutError] = useState(false);
  const [networkConnected, setNetworkConnected] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    if (!user?.id) return;
    const repository = getRepository();
    const sync = (force = false) => {
      void flushOutbox(repository, user.id, { force }).catch(() => undefined);
    };

    sync();
    let remove: (() => Promise<void>) | null = null;
    void Network.getStatus()
      .then(({ connected }) => setNetworkConnected(connected))
      .catch(() => undefined);
    void Network.addListener("networkStatusChange", ({ connected }) => {
      setNetworkConnected(connected);
      if (connected) sync(true);
    }).then((handle) => {
      remove = () => handle.remove();
    });
    const interval = window.setInterval(() => sync(), 30_000);
    return () => {
      window.clearInterval(interval);
      if (remove) void remove();
    };
  }, [user?.id]);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <aside className="sidebar">
        <Brand inverse />
        <nav className="sidebar__nav" aria-label="Primary navigation">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className="nav-link">
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__account">
          <strong>{user?.displayName}</strong>
          <span>{user?.email}</span>
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setSignOutError(false);
              void signOut().catch(() => setSignOutError(true));
            }}
          >
            Sign out
          </button>
          {signOutError && <span role="alert">Sign-out failed. Try again in Settings.</span>}
        </div>
      </aside>
      <header className="mobile-header">
        <Brand compact />
        <Link
          to="/settings"
          className="mobile-header__profile"
          aria-label={`Open settings for ${user?.displayName ?? "your account"}`}
        >
          <SettingsIcon />
          <span>Settings</span>
        </Link>
      </header>
      <UpdatePrompt />
      <main id="main-content" className="main-content" tabIndex={-1}>
        {!networkConnected && (
          <div className="offline-banner" role="status" aria-live="polite">
            <strong>Offline.</strong>
            <span>
              Voice capture and drafts stay on this device. Account search and saving resume when
              connected.
            </span>
          </div>
        )}
        {children}
      </main>
      <nav className="mobile-nav" aria-label="Primary navigation">
        {mobileNavItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={
              to === "/capture" ? "mobile-nav__link mobile-nav__link--capture" : "mobile-nav__link"
            }
          >
            <Icon />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
